import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdRefresh, MdOpenInNew, MdAdd } from 'react-icons/md';
import { helpdeskTicketApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import './HelpdeskMyTickets.css';

const STATUS_OPTIONS = ['ALL', 'OPEN', 'ASSIGNED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'REOPENED'];
const ID_CARD_STATUS_OPTIONS = ['ALL', 'SUBMITTED', 'DATA_VERIFIED', 'PHOTO_APPROVED', 'PRINT_QUEUE', 'PRINTING', 'PRINT_READY', 'DELIVERED', 'REJECTED'];

function HelpdeskMyTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [idCardStatusFilter, setIdCardStatusFilter] = useState('ALL');

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await helpdeskTicketApi.getMyTickets();
      setTickets(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const getStatusColor = (status) => {
    const colors = {
      OPEN: '#2196F3', ASSIGNED: '#FF9800', IN_PROGRESS: '#9C27B0',
      RESOLVED: '#4CAF50', CLOSED: '#607D8B', REOPENED: '#F44336'
    };
    return colors[status] || '#666';
  };

  const getPriorityColor = (priority) => {
    const colors = { LOW: '#4CAF50', MEDIUM: '#FF9800', HIGH: '#f44336', CRITICAL: '#d32f2f' };
    return colors[priority] || '#666';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const filteredTickets = tickets.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      t.ticketNumber?.toLowerCase().includes(q) ||
      t.title?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'ALL' || t.ticketStatus === statusFilter;
    const matchesIdCardStatus = idCardStatusFilter === 'ALL' || t.idCardStatus === idCardStatusFilter;
    return matchesSearch && matchesStatus && matchesIdCardStatus;
  });

  return (
    <div className="helpdesk-my-tickets">
      <div className="page-header">
        <h2>My Helpdesk Tickets</h2>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadTickets}><MdRefresh /> Refresh</button>
          <button className="btn btn-primary" onClick={() => navigate('/user/helpdesk/submit')}>
            <MdAdd /> New Request
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by ticket # or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All Status' : s.replace('_', ' ')}</option>)}
        </select>
        <select value={idCardStatusFilter} onChange={e => setIdCardStatusFilter(e.target.value)}>
          {ID_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All ID Card Status' : s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th></th>
              <th>Ticket #</th>
              <th>Date</th>
              <th>Type</th>
              <th>Title</th>
              <th>Priority</th>
              <th>Status</th>
              <th>ID Card Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array(5).fill(null).map((_, i) => (
                <tr key={i}><td colSpan={9}><div className="skeleton-row" /></td></tr>
              ))
            ) : filteredTickets.length === 0 ? (
              <tr><td colSpan={9} className="empty-message">
                {tickets.length === 0 ? 'No tickets yet. Submit your first request!' : 'No matching tickets found'}
              </td></tr>
            ) : (
              filteredTickets.map((ticket, idx) => (
                <tr key={ticket.id}>
                  <td>{idx + 1}</td>
                  <td>
                    <button className="btn-icon btn-open" onClick={() => navigate(`/user/helpdesk/tickets/${ticket.id}`)} title="View">
                      <MdOpenInNew />
                    </button>
                  </td>
                  <td className="ticket-number">{ticket.ticketNumber}</td>
                  <td>{formatDate(ticket.createdAt)}</td>
                  <td><span className="type-badge">{ticket.requestType === 'GENERAL_REQUEST' ? 'General' : 'ID Card'}</span></td>
                  <td className="ticket-title">{ticket.title || '-'}</td>
                  <td>
                    {ticket.priority && (
                      <span className="priority-badge" style={{ background: getPriorityColor(ticket.priority) + '20', color: getPriorityColor(ticket.priority) }}>
                        {ticket.priority}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="status-badge" style={{ background: getStatusColor(ticket.ticketStatus) + '20', color: getStatusColor(ticket.ticketStatus) }}>
                      {ticket.ticketStatus?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    {ticket.requestType === 'ID_CARD_REQUEST' && ticket.idCardStatus ? (
                      <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>
                        {ticket.idCardStatus?.replace('_', ' ')}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HelpdeskMyTickets;
